/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import GetBadgesInfo from './rendersequence/GetBadgesInfo';
import RenderBadgesInfo from './rendersequence/RenderBadgesInfo';
import StringResources from './nls/StringResources';
const Deferred = require('lib/@waca/core-client/js/core-client/utils/Deferred');

const MAX_DRIVERS_PER_COLUMN  = 5;
//Temporary until we decide how we are rendering the info
const ACCURACY_RANGES = [
	/*
	{
		min:2,
		msgs:['badge_significantly_correlated_one_driver', 'badge_significantly_correlated_one_driver']
	},
	{
		min:2,
		msgs:['badge_strongly_correlated_one_driver', 'badge_strongly_correlated_two_driver']
	},
	{
		min:2,
		msgs:['badge_positively_correlated_one_driver','badge_positively_correlated_two_driver']
	},
	*/
	{
		min:0.66,
		msgs:['badge_correlated_one_driver', 'badge_correlated_two_driver']
	}
];

class BadgesFeature {

	constructor (widgetApi, dashboardApi) {
		this.badgeDataReadyDfd = new Deferred();
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;
		this.badgeDataBySlots = {};
		this.badgeClickHandlers = [];
		this._canRender = false;
		// todo: no equivalent new api for widgetApi.on;
		this.widgetApi.on('visevent:customdataselection', this._onVisBadgeClick, this);
		this.dashboardApi.getCanvasWhenReady().then((canvas) => {
			this.layoutApi = canvas.getLayout(this.widgetApi.getId());
			this.layoutApi.onTitleBadgeClick(this._onTitleBadgeClick, this);
			this._canRender = true;
			// Render the badges if If we have requested to render the badges before the board is loaded.
			if(this._isBadgesRendered){
				this.renderBadges();
			}
		});

	}

	getFeatureTags() {
		return ['interactive-only'];
	}

	destroy() {
		// todo: no equivalent new api for widgetApi.off;
		this.widgetApi.off('visevent:customdataselection', this._onVisBadgeClick, this);
		if (this.layoutApi) {
			this.layoutApi.offTitleBadgeClick(this._onTitleBadgeClick, this);
		}
		this.badgeClickHandlers = []; //remove all references
	}

	/**
	* Set the badge info for a given column
	*
	* @param {String} columnId - metadata column id
	* @param {String} label - metadata column label
	* @param {integer} badgeSlotIndex - the index of the slot that the badge is associated with. If the badge is associated with a slot that does not support badges, the index will be -1
	* @param {String} slotId - the slot id
	* @param {Object} data - fast pattern detection data response
	*
	*/
	setBadgeData(columnId, label, badgeSlotIndex, slotId, data) {
		let badgesData = this.badgeDataBySlots[badgeSlotIndex];
		if (!badgesData) {
			badgesData = {
				slotId: slotId,
				data: {}
			};
			this.badgeDataBySlots[badgeSlotIndex] = badgesData;
		}

		const badgeData = this._createBadgeData(columnId, label, data);
		if (badgeData) {
			badgesData.data[columnId] = badgeData;
			this._calculateBadgeDecorationLabel(badgesData);
			this._invokeChange();
			if (this.badgeDataReadyDfd){
				this.badgeDataReadyDfd.resolve();
			}
		}
	}

	/**
	* Clear the badge info
	*/
	clearBadgesInfo() {
		//todo: new decoratorApi not working yet
		const decoratorApi = this.widgetApi.getVisApi().getDecoratorAPI(0);
		if (decoratorApi) {
			decoratorApi.clearItemDecoration('badge');
			decoratorApi.updateDecorations();
		}

		if (this.layoutApi) {
			this.layoutApi.setTitleBadge(null);
		}

		this._isBadgesRendered = false;
		this.badgeDataBySlots = {};
		this.badgeDataReadyDfd = new Deferred();
	}

	/*
	* Return the badge info associated with the widget title
	*
	*/
	getTitleBadgeInfo() {
		return this.getBadgesInfoForSlot(-1);
	}

	/**
	* return the badge Info associated with a given column od
	* @param {integer} slotIndex - slotIndex
	*/
	getBadgesInfoForSlot(slotIndex) {
		let badgesInfo;
		if (this.badgeDataBySlots[slotIndex]) {
			badgesInfo = this.badgeDataBySlots[slotIndex].data;
		} else {
			badgesInfo = {};
		}
		// Return a copy of the internal badges data map
		return JSON.parse(JSON.stringify(badgesInfo));
	}

	/**
	* return the badge Info associated with a given column od
	* @param {integer} slotId - Slot id
	*/
	getBadgesInfoForSlotBySlotId(slotId) {
		let index;
		let badgesInfo;
		for (index in this.badgeDataBySlots) {
			if (this.badgeDataBySlots[index].slotId === slotId) {
				badgesInfo = this.badgeDataBySlots[index].data;
				break;
			}
		}

		// Return a copy of the internal badges data map
		return JSON.parse(JSON.stringify(badgesInfo || {}));
	}

	/**
	* Register a handler that will be called when the badge info has changes
	*/
	onBadgeSelect(handler) {
		this.badgeClickHandlers.push(handler);
	}

	/**
	* Unregister a previously registered handler using onChange()
	*/
	offBadgeSelect(handler) {
		const index = this.badgeClickHandlers.indexOf(handler);
		if (index > -1) {
			this.badgeClickHandlers.splice(index, 1);
		}
	}

	/**
	* Return the feature API object.
	*/
	getAPI() {
		return {
			onBadgeSelect: this.onBadgeSelect.bind(this),
			offBadgeSelect: this.offBadgeSelect.bind(this),
			getBadgeDataWhenReady: this.getBadgeDataWhenReady.bind(this)
		};
	}

	/**
	* Return the feature extra render sequence steps
	*/
	getExtraRenderSequenceSteps() {
		return [{
			id: 'getBadgeInfo',
			module: GetBadgesInfo,
			moduleOptions:{
				badgesFeature: this
			}
		},{
			id: 'renderBadgeInfo',
			dependencies:['getBadgeInfo', 'render'],
			module: RenderBadgesInfo,
			moduleOptions:{
				badgesFeature: this
			}
		}];
	}

	renderBadges() {
		this._isBadgesRendered = true;
		// guard to wait for the board to be loaded.
		if (this._canRender) {
			this._decorateSlots();
			this._decorateTitle();
		}
	}
	/**
	 * @param {slotId} slot id of the widget, -1 means title
	 * @return {object} promise with badge info
	 */
	getBadgeDataWhenReady(slotId){
		return this.badgeDataReadyDfd.promise.then(() => this.getBadgesInfoForSlotBySlotId(slotId));
	}

	/**
	* decorate the visualization with the supported badges
	*/
	_decorateSlots() {
		let slotIndex;
		let isDecorated;
		//todo: new decoratorApi not working yet
		const decoratorApi = this.widgetApi.getVisApi().getDecoratorAPI(0);
		if (decoratorApi) {
			for (slotIndex in this.badgeDataBySlots) {
				if (slotIndex >= 0) {
					const label = this.badgeDataBySlots[slotIndex].label;
					if (label) {
						decoratorApi.decorateItem(slotIndex, 'badge', label);
						isDecorated = true;
					}
				}
			}
			if (isDecorated) {
				decoratorApi.updateDecorations();
			}
		}
	}

	/**
	* decorate the widget title with the the title badge info
	*/
	_decorateTitle() {
		if (this.badgeDataBySlots[-1] && this.layoutApi) {
			this.layoutApi.setTitleBadge(this.badgeDataBySlots[-1].label, StringResources.get('badge_label'));
		}
	}

	_getSlotId(badgePayload){
		return badgePayload.dataItem && badgePayload.dataItem.itemClass ?
			badgePayload.dataItem.itemClass.slotDef && badgePayload.dataItem.itemClass.slotDef.name
			: badgePayload.dataItem && badgePayload.dataItem.slotDef && badgePayload.dataItem.slotDef.name;
	}
	/**
	* Called when a vis badge is clicked.
	* Call all the registered onClick handler with the proper badge info
	*
	* @param {object} payload - badge selection payload form the visAPI
	*/
	_onVisBadgeClick(payload) {
		const item =  payload.items[0];
		if (item && item.key === 'badge' &&  item.values && item.values.length) {
			const badgePayload = item.values[0].payload;
			const slotId = this._getSlotId(badgePayload);
			if (slotId){
				const clientRect = payload.event.target.getBoundingClientRect();
				const slotBadgeInfo = this.getBadgesInfoForSlotBySlotId(slotId);
				this.badgeClickHandlers.forEach((handler) => {
					handler(slotBadgeInfo, clientRect);
				});
			}
		}
	}

	/**
	* Called when a title badge is clicked.
	* Call all the registere onClick handler with the proper badge info
	* @param {object} payload - badge selection payload
	*/
	_onTitleBadgeClick(payload) {
		const clientRect= payload.event.target.getBoundingClientRect();
		const badgeInfo = this.getTitleBadgeInfo();
		this.badgeClickHandlers.forEach((handler) => {
			handler(badgeInfo, clientRect);
		});
	}

	/**
	* called when a new badge info is available.
	* If a render has been requested, this method will render the badges as they become available
	*/
	_invokeChange(){
		// if we have rendered.. render again to update
		if (this._isBadgesRendered) {
			this.renderBadges();
		}
	}

	/**
	* Calculate the badge decoration label
	* @param {object}  badgesData
	*/
	_calculateBadgeDecorationLabel(badgesData) {
		let name;
		let count = 0;
		for (name in badgesData.data) {
			count += badgesData.data[name].drivers.length;
		}
		badgesData.label = count > 0 ? count : '';
	}

	/**
	* Create the badge entry detail. This function will:
	* 1 - Sort the drivers by accuracy.
	* 2 - Only keep up to 5 drivers
	* 3 - Filter out any drivers that are below the threshhold
	* 4 - Build a label that contains the drivers and the formatted accuracy
	*
	*	@param {String} columnId
	* @param {String} label
	* @param {Object} data - fast pattern detection data response
	*/
	_createBadgeData(columnId, label, data) {
		const badgeData = {
			id: columnId,
			label: label,
			drivers:[]
		};

		if (data.results) {
			data.results.sort((a, b) => { return b.accuracy - a.accuracy;})
				.forEach((entry, index) => {
					if (index >= MAX_DRIVERS_PER_COLUMN){
						return;
					}
					const driverInfo = this._createDriverInfo(entry, columnId);
					if (driverInfo){
						badgeData.drivers.push(driverInfo);
					}
				});
		}

		return badgeData.drivers.length ? badgeData : null ;
	}

	_getAllFiltersAsLocalFilters(){
		let localFilters = [];
		let globalFilters = [];
		const visualization = this.widgetApi.getVisualization();
		if (visualization){
			localFilters = visualization.getLocalFilters().getFilterList();
			const globalFiltersFeature = this.dashboardApi.getFeature('GlobalFilters');
			if (globalFiltersFeature){
				const sourceId = visualization.getDataSource() && visualization.getDataSource().getId();
				const scope = this.widgetApi.getScope();
				globalFilters = globalFiltersFeature.getFilters(sourceId, scope).getFilterList();
			}
		}
		return [...localFilters, ...globalFilters];
	}

	/**
	* Create a driver info. This method will generate a label
	* that contains the list of drivers and accuracy.
	* Any driver with an accuracy below the pre defined threshold will be filtered out. In the case the response will be null.
	*
	* @param {Object} driverData - fast pattern detection driver data response
	*/
	_createDriverInfo(driverData, columnId) {

		let driverInfo = null;
		const range = ACCURACY_RANGES.find((range) => {
			return driverData.accuracy > range.min;
		});
		if (range) {
			const visualization = this.widgetApi.getVisualization();
			const dataSource = visualization && visualization.getDataSource();
			const sourceId =  dataSource && dataSource.getId();
			const inputs = driverData.fields.inputs;
			driverInfo = {
				accuracy: driverData.accuracy,
				inputs: inputs,
				target: columnId,
				widgetSpec: {
					type:'live',
					visId:'com.ibm.vis.rave2comet',
					visTypeLocked: true,
					slotmapping:{
						slots:[{
							name:'values',
							dataItems:['1']
						}]
					},
					data:{
						dataViews:[{
							modelRef: sourceId,
							dataItems:[{
								itemId: columnId,
								id: '1'
							}]
						}]
					},
					localFilters: this._getAllFiltersAsLocalFilters(),
					'customData': {
						'selected': [{
							'id': driverData.id,
							'value': true
						}]
					}
				}
			};
			const options = this._createLabelOptionsForDriver(driverInfo);
			let msgIndex = Math.min(inputs.length - 1, 1);
			driverInfo.label = StringResources.get(range.msgs[msgIndex], options);
		}
		return driverInfo;
	}

	/**
	*
	* Create the string resources options that will be used to generate the driver label.
	* The logic handles whether we have 1 , 2 or more drivers and depedening on the number it will select the proper message options.
	*
	*/
	_createLabelOptionsForDriver(driverInfo) {
		const options = {};
		options.percent  = (driverInfo.accuracy * 100 ).toFixed(0) + '%';

		if (driverInfo.inputs.length > 2) {
			// We have more than 2 drivers.
			const labels = driverInfo.inputs.map((input) => {return input.label;});
			const last = labels.pop();
			options['column0'] = labels.toString().replace(/,/g,', ');
			labels.shift();
			options['column1'] = last;
		} else {
			driverInfo.inputs.forEach((input, index) => {
				options['column' + index] = input.label;
			});
		}

		return options;
	}
}
export default BadgesFeature;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-badges/src/js/BadgesFeature.js